import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { NotificationItemCard, type NotificationItem } from '@/entities/notification'
import { cx } from '@/shared/lib'
import { Alert } from '@/shared/ui/Alert'
import { Button } from '@/shared/ui/Button'
import { EmptyState } from '@/shared/ui/EmptyState'
import { Skeleton } from '@/shared/ui/Skeleton'
import s from './NotificationBell.module.css'

/** Что каркас отдаёт слоту ссылки «Все уведомления»: раскладывается на `Link` роутера. */
export type NotificationLinkSlotProps = {
  className: string
  children: ReactNode
  onClick: () => void
}

export type NotificationPanelProps = {
  items: NotificationItem[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  /** Ссылку рисует потребитель: каркас о роутере не знает. */
  renderViewAllLink: (props: NotificationLinkSlotProps) => ReactNode
  /** Выбор пункта закрывает панель. */
  onNavigate: () => void
}

const SKELETON_ROWS = [0, 1, 2] as const

/**
 * Содержимое выпадающей ленты колокольчика: заголовок, последние уведомления и ссылка на полный
 * список. Презентационно — данные приносит `NotificationBell`. Строки — та же карточка сущности,
 * что и на странице. Все четыре состояния: загрузка (скелетон), ошибка (повтор), пусто, лента.
 */
export function NotificationPanel({
  items,
  isLoading,
  isError,
  onRetry,
  renderViewAllLink,
  onNavigate,
}: NotificationPanelProps) {
  const { t } = useTranslation('notifications')

  return (
    <section className={s.panel} aria-label={t('bell.heading')}>
      <h2 className={s.heading}>{t('bell.heading')}</h2>

      <div className={s.body}>
        {isLoading ? (
          <div className={s.loading} aria-busy="true" aria-label={t('bell.loading')}>
            {SKELETON_ROWS.map((row) => (
              <Skeleton key={row} shape="block" />
            ))}
          </div>
        ) : isError ? (
          <Alert
            tone="danger"
            announce="polite"
            action={
              <Button size="sm" variant="secondary" onClick={onRetry}>
                {t('bell.retry')}
              </Button>
            }
          >
            {t('bell.error')}
          </Alert>
        ) : items.length === 0 ? (
          <EmptyState heading={t('bell.empty')} headingLevel={3} size="sm" />
        ) : (
          <ul className={s.list} role="list">
            {items.map((item) => (
              <li key={item.id}>
                <NotificationItemCard item={item} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {renderViewAllLink({
        className: cx(s.viewAll),
        onClick: onNavigate,
        children: t('bell.viewAll'),
      })}
    </section>
  )
}
